import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Modal,
    ModalBody,
    Button,
    Input,
    Table,
    Form,
} from "reactstrap";
import { toast } from "react-toastify";
import {
    getCandidateWorkExperienceByCandidateId,
    updateCandidateWorkExperienceByCandidateId,
} from "model/CandidateModel";

const JOB_TYPE_SELECT_OPTIONS = ["Driver", "Non Driver"];

const generateLocalWorkExperienceRowKey = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `local-row-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const normalizeServerExperienceItemId = (rawExperienceItemId) => {
    if (rawExperienceItemId == null || rawExperienceItemId === "") return null;
    const trimmed = String(rawExperienceItemId).trim();
    if (!trimmed || trimmed.toLowerCase() === "null") return null;
    return trimmed;
};

const createEmptyWorkExperienceFormRow = () => ({
    localRowKey: generateLocalWorkExperienceRowKey(),
    serverExperienceItemId: null,
    companyName: "",
    position: "",
    duration: "",
    jobType: "Driver",
});

/**
 * Maps one API `items[]` entry into internal form row shape.
 * @param {object} workExperienceApiItem
 * @param {number} itemIndex
 */
const mapWorkExperienceApiItemToFormRow = (workExperienceApiItem, itemIndex = 0) => {
    const serverId = normalizeServerExperienceItemId(
        workExperienceApiItem?.id ?? workExperienceApiItem?._id
    );
    return {
        localRowKey:
            serverId != null
                ? `server-${serverId}`
                : `api-row-${itemIndex}-${generateLocalWorkExperienceRowKey()}`,
        serverExperienceItemId: serverId,
        companyName: String(workExperienceApiItem?.companyName ?? "").trim(),
        position: String(workExperienceApiItem?.position ?? "").trim(),
        duration: String(workExperienceApiItem?.duration ?? "").trim(),
        jobType: String(workExperienceApiItem?.jobType ?? "").trim() || "Driver",
    };
};

/**
 * Pulls `items` from GET `candidate/WorkExperience/{candidateId}` envelope.
 */
const extractWorkExperienceItemsFromApiResponse = (apiResponse) => {
    const payload = apiResponse?.data ?? apiResponse;
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== "object") return [];
    if (Array.isArray(payload.items)) return payload.items;
    const nested = payload?.data ?? payload?.record;
    if (Array.isArray(nested)) return nested;
    if (Array.isArray(nested?.items)) return nested.items;
    if (Array.isArray(payload?.details)) return payload.details;
    return [];
};

const extractCandidateClientIdFromWorkExperienceApiResponse = (apiResponse) => {
    const payload = apiResponse?.data ?? apiResponse;
    if (!payload || typeof payload !== "object") return "";
    return String(payload.candidateClientId ?? "").trim();
};

const formRowHasAnyVisibleWorkExperienceText = (formRow) =>
    Boolean(
        String(formRow?.companyName ?? "").trim() ||
            String(formRow?.position ?? "").trim() ||
            String(formRow?.duration ?? "").trim()
    );

/** Only rows still on screen; empty placeholders are omitted. */
const buildWorkExperienceItemsPayloadFromFormRows = (formRows = []) =>
    (Array.isArray(formRows) ? formRows : [])
        .filter(
            (formRow) =>
                formRowHasAnyVisibleWorkExperienceText(formRow) ||
                Boolean(formRow?.serverExperienceItemId)
        )
        .map((formRow) => ({
            id: formRow.serverExperienceItemId
                ? String(formRow.serverExperienceItemId)
                : "",
            companyName: String(formRow.companyName ?? "").trim(),
            position: String(formRow.position ?? "").trim(),
            duration: String(formRow.duration ?? "").trim(),
            jobType: String(formRow.jobType ?? "").trim(),
            isDeleted: false,
        }));

const isSaveSuccess = (apiResponse) =>
    apiResponse?.success ||
    apiResponse?.isSuccess ||
    apiResponse?.status === "success" ||
    apiResponse?.status === true;

/**
 * GET/PUT `candidate/WorkExperience/{candidateId}` with body `{ candidateClientId, items }` on save.
 *
 * @param {object} [candidateListRowOrDetailsItem] — e.g. `candidateDetails`; uses `candidateId` for path when set.
 * @param {string} [workExperiencePathCandidateIdFallback] — route `id` until `candidateId` is available.
 * @param {function} [setIsDriverExperience] — optional parent callback after successful save.
 */
const DrivingExperienceModal = ({
    isOpen,
    toggle,
    candidateListRowOrDetailsItem,
    workExperiencePathCandidateIdFallback,
    onClose,
    setIsDriverExperience,
}) => {
    const workExperienceApiCandidateId = useMemo(() => {
        const candidateIdFromListOrDetailsItem = String(
            workExperiencePathCandidateIdFallback ?? ""
        ).trim();
        if (candidateIdFromListOrDetailsItem) {
            return candidateIdFromListOrDetailsItem;
        }
        return String(workExperiencePathCandidateIdFallback ?? "").trim();
    }, [
        candidateListRowOrDetailsItem?.candidateId,
        workExperiencePathCandidateIdFallback,
    ]);

    const candidateClientIdFallbackFromDetails = useMemo(
        () => String(candidateListRowOrDetailsItem?.id ?? "").trim(),
        [candidateListRowOrDetailsItem?.id]
    );

    const [workExperienceFormRows, setWorkExperienceFormRows] = useState([
        createEmptyWorkExperienceFormRow(),
    ]);
    const [workExperienceCandidateClientIdForSubmit, setWorkExperienceCandidateClientIdForSubmit] =
        useState("");
    const [workExperienceListLoading, setWorkExperienceListLoading] = useState(false);
    const [workExperienceSaveInProgress, setWorkExperienceSaveInProgress] = useState(false);
    const [hadMeaningfulWorkExperienceRowsWhenModalOpened, setHadMeaningfulWorkExperienceRowsWhenModalOpened] =
        useState(false);

    const resetWorkExperienceFormToSingleEmptyRow = () =>
        setWorkExperienceFormRows([createEmptyWorkExperienceFormRow()]);

    const loadWorkExperienceFromApi = useCallback(async () => {
        if (!workExperienceApiCandidateId) return;
        setWorkExperienceListLoading(true);
        try {
            const workExperienceApiResponse = await getCandidateWorkExperienceByCandidateId({
                candidateId: workExperienceApiCandidateId,
            });
            const candidateClientIdFromEnvelope =
                extractCandidateClientIdFromWorkExperienceApiResponse(workExperienceApiResponse);
            setWorkExperienceCandidateClientIdForSubmit(
                candidateClientIdFromEnvelope || candidateClientIdFallbackFromDetails
            );

            const experienceItemsFromApi =
                extractWorkExperienceItemsFromApiResponse(workExperienceApiResponse);
            const mappedFormRows = experienceItemsFromApi.length
                ? experienceItemsFromApi.map((apiItem, itemIndex) =>
                      mapWorkExperienceApiItemToFormRow(apiItem, itemIndex)
                  )
                : [createEmptyWorkExperienceFormRow()];
            setWorkExperienceFormRows(mappedFormRows);

            const anyMeaningfulRow = experienceItemsFromApi.some((apiItem, itemIndex) =>
                formRowHasAnyVisibleWorkExperienceText(
                    mapWorkExperienceApiItemToFormRow(apiItem, itemIndex)
                )
            );
            setHadMeaningfulWorkExperienceRowsWhenModalOpened(anyMeaningfulRow);
        } catch {
            resetWorkExperienceFormToSingleEmptyRow();
            setWorkExperienceCandidateClientIdForSubmit(candidateClientIdFallbackFromDetails);
            setHadMeaningfulWorkExperienceRowsWhenModalOpened(false);
        } finally {
            setWorkExperienceListLoading(false);
        }
    }, [workExperienceApiCandidateId, candidateClientIdFallbackFromDetails]);

    useEffect(() => {
        if (!isOpen || !workExperienceApiCandidateId) return;
        loadWorkExperienceFromApi();
    }, [isOpen, workExperienceApiCandidateId, loadWorkExperienceFromApi]);

    const closeDrivingExperienceModal = (workExperienceModalSavedSuccessfully = false) => {
        onClose?.(workExperienceModalSavedSuccessfully, hadMeaningfulWorkExperienceRowsWhenModalOpened);
        toggle();
    };

    const handleWorkExperienceFieldChange = (rowIndex, fieldName, nextValue) => {
        setWorkExperienceFormRows((previousRows) =>
            previousRows.map((row, index) =>
                index === rowIndex ? { ...row, [fieldName]: nextValue } : row
            )
        );
    };

    const handleAddWorkExperienceRow = () => {
        setWorkExperienceFormRows((previousRows) => [
            ...previousRows,
            createEmptyWorkExperienceFormRow(),
        ]);
    };

    const handleRemoveWorkExperienceRow = (rowIndex) => {
        setWorkExperienceFormRows((previousRows) => {
            const nextRows = previousRows.filter((_, index) => index !== rowIndex);
            return nextRows.length ? nextRows : [createEmptyWorkExperienceFormRow()];
        });
    };

    const handleWorkExperienceFormSubmit = async (submitEvent) => {
        submitEvent.preventDefault();
        if (!workExperienceApiCandidateId) {
            toast.error("Candidate is required");
            return;
        }
        const resolvedCandidateClientId = String(
            workExperienceCandidateClientIdForSubmit || candidateClientIdFallbackFromDetails || ""
        ).trim();
        if (!resolvedCandidateClientId) {
            toast.error("Candidate client id is required to save work experience.");
            return;
        }

        const submitPayload = {
            candidateClientId: resolvedCandidateClientId,
            items: buildWorkExperienceItemsPayloadFromFormRows(workExperienceFormRows),
        };

        setWorkExperienceSaveInProgress(true);
        try {
            const saveResponse = await updateCandidateWorkExperienceByCandidateId(
                workExperienceApiCandidateId,
                submitPayload
            );
            if (isSaveSuccess(saveResponse)) {
                toast.success(saveResponse?.message || "Saved successfully");
                setIsDriverExperience?.(true);
                closeDrivingExperienceModal(true);
            } else {
                toast.error(saveResponse?.message || "Save failed");
            }
        } finally {
            setWorkExperienceSaveInProgress(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={() => closeDrivingExperienceModal(false)}
            backdrop="static"
            size="xl"
            centered
            zIndex={5000}
            className="modal-full-height driving-experience-modal"
        >
            <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
                <Form onSubmit={handleWorkExperienceFormSubmit}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-bold mb-0 text-uppercase">Driving Experience</h5>
                        <Button close onClick={() => closeDrivingExperienceModal(false)} />
                    </div>

                    <hr />

                    <div className="text-end mt-3">
                        <Button color="primary" type="button" onClick={handleAddWorkExperienceRow}>
                            + Add
                        </Button>
                    </div>

                    <Table responsive bordered className="mt-3">
                        <thead>
                            <tr>
                                <th>Company</th>
                                <th>Position</th>
                                <th>Duration</th>
                                <th>Job type</th>
                                <th className="text-center col-actions">#</th>
                            </tr>
                        </thead>
                        <tbody>
                            {workExperienceListLoading ? (
                                <tr>
                                    <td colSpan="5" className="text-center text-muted py-4">
                                        Loading…
                                    </td>
                                </tr>
                            ) : (
                                workExperienceFormRows.map((formRow, rowIndex) => (
                                    <tr key={formRow.localRowKey}>
                                        <td>
                                            <Input
                                                type="text"
                                                value={formRow.companyName}
                                                onChange={(changeEvent) =>
                                                    handleWorkExperienceFieldChange(
                                                        rowIndex,
                                                        "companyName",
                                                        changeEvent.target.value
                                                    )
                                                }
                                                placeholder="Company name"
                                            />
                                        </td>
                                        <td>
                                            <Input
                                                type="text"
                                                value={formRow.position}
                                                onChange={(changeEvent) =>
                                                    handleWorkExperienceFieldChange(
                                                        rowIndex,
                                                        "position",
                                                        changeEvent.target.value
                                                    )
                                                }
                                                placeholder="Position"
                                            />
                                        </td>
                                        <td>
                                            <Input
                                                type="text"
                                                value={formRow.duration}
                                                onChange={(changeEvent) =>
                                                    handleWorkExperienceFieldChange(
                                                        rowIndex,
                                                        "duration",
                                                        changeEvent.target.value
                                                    )
                                                }
                                                placeholder="Duration"
                                            />
                                        </td>
                                        <td className="align-middle">
                                            <Input
                                                type="select"
                                                value={formRow.jobType || "Driver"}
                                                onChange={(changeEvent) =>
                                                    handleWorkExperienceFieldChange(
                                                        rowIndex,
                                                        "jobType",
                                                        changeEvent.target.value
                                                    )
                                                }
                                            >
                                                {[
                                                    ...new Set(
                                                        [...JOB_TYPE_SELECT_OPTIONS, formRow.jobType].filter(
                                                            Boolean
                                                        )
                                                    ),
                                                ].map((jobTypeOption) => (
                                                    <option key={jobTypeOption} value={jobTypeOption}>
                                                        {jobTypeOption}
                                                    </option>
                                                ))}
                                            </Input>
                                        </td>
                                        <td className="col-actions text-center vertical-allign-middle">
                                            <i
                                                className="mdi mdi-trash-can font-size-18 text-danger cursor-pointer"
                                                role="button"
                                                tabIndex={0}
                                                aria-label="Delete work experience row"
                                                onClick={() => handleRemoveWorkExperienceRow(rowIndex)}
                                                onKeyDown={(keyboardEvent) => {
                                                    if (
                                                        keyboardEvent.key === "Enter" ||
                                                        keyboardEvent.key === " "
                                                    ) {
                                                        keyboardEvent.preventDefault();
                                                        handleRemoveWorkExperienceRow(rowIndex);
                                                    }
                                                }}
                                            />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>

                    <div className="text-end mt-3">
                        <Button
                            color="primary"
                            type="submit"
                            disabled={workExperienceListLoading || workExperienceSaveInProgress}
                        >
                            {workExperienceSaveInProgress ? "Saving…" : "Save"}
                        </Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default DrivingExperienceModal;
